import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import SecondaryButton from '../atoms/SecondaryButton'
import { MotorbikePhoto } from 'src/redux/photosReducer'

interface PromotionPhotosModalProps {
  photos: MotorbikePhoto[]
  selectedIds: string[]
  coverId: string | null
  maxImages: number | null
  onClose: () => void
  onConfirm: (ids: string[], coverId: string | null) => void
}

const PromotionPhotosModal = ({
  photos,
  selectedIds,
  coverId,
  maxImages,
  onClose,
  onConfirm
}: PromotionPhotosModalProps): JSX.Element => {
  const { t } = useTranslation()
  const [draft, setDraft] = useState<string[]>(selectedIds)
  const firstSelected = photos.find((photo) => selectedIds.includes(photo.id))?.id ?? null
  const [draftCover, setDraftCover] = useState<string | null>(coverId ?? firstSelected)
  const limitReached = maxImages !== null && draft.length >= maxImages

  const coverAfter = (ids: string[], current: string | null): string | null => {
    if (current !== null && ids.includes(current)) {
      return current
    }
    return photos.find((photo) => ids.includes(photo.id))?.id ?? null
  }

  const toggle = (photoId: string): void => {
    if (draft.includes(photoId)) {
      const ids = draft.filter((id) => id !== photoId)
      setDraft(ids)
      setDraftCover(coverAfter(ids, draftCover === photoId ? null : draftCover))
      return
    }
    if (limitReached) {
      return
    }
    const ids = [...draft, photoId]
    setDraft(ids)
    setDraftCover(draftCover ?? photoId)
  }

  const confirm = (): void => {
    const ordered = photos.filter((photo) => draft.includes(photo.id)).map((photo) => photo.id)
    const nextCover = draftCover !== null && ordered.includes(draftCover) ? draftCover : ordered[0] ?? null
    onConfirm(ordered, nextCover)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="rounded-lg p-6 max-w-3xl w-full mx-4 bg-white">
        <div className="text-xl font-semibold font-Montserrat mb-2">{t('my_vehicles.promotion_photos')}</div>
        <div className="text-sm font-Montserrat text-foregrounds-fg-subtle mb-4">
          {maxImages === null
            ? t('my_vehicles.promotion_photos_hint')
            : t('my_vehicles.promotion_photos_limit', { count: maxImages })}
        </div>
        {photos.length === 0 ? (
          <div className="text-sm font-medium font-Montserrat text-foregrounds-fg-subtle mb-6">
            {t('my_vehicles.promotion_photos_empty')}
          </div>
        ) : (
          <div className="flex flex-wrap gap-3 mb-6">
            {photos.map((photo) => {
              const selected = draft.includes(photo.id)
              const disabled = limitReached && !selected
              const isCover = selected && photo.id === draftCover
              return (
                <div
                  key={photo.id}
                  className={`relative w-28 h-20 rounded-lg overflow-hidden ${
                    selected ? 'outline outline-2 outline-Estigroup-primary' : ''
                  } ${disabled ? 'opacity-40' : ''}`}>
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      toggle(photo.id)
                    }}
                    className="w-28 h-20">
                    <img src={photo.thumb_url ?? photo.url ?? ''} alt="" className="w-28 h-20 object-cover bg-white" />
                  </button>
                  {isCover && (
                    <span className="absolute left-1 top-1 rounded bg-Estigroup-primary px-1 text-[10px] font-semibold leading-4 text-white">
                      {t('my_vehicles.promotion_photo_cover')}
                    </span>
                  )}
                  {selected && !isCover && (
                    <button
                      type="button"
                      onClick={() => {
                        setDraftCover(photo.id)
                      }}
                      className="absolute inset-x-1 bottom-1 truncate rounded bg-white/90 px-1 text-[10px] font-semibold leading-4 text-foregrounds-fg-base">
                      {t('my_vehicles.promotion_photo_make_cover')}
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )}
        <div className="flex justify-end gap-3">
          <SecondaryButton size="base" onClick={onClose}>
            {t('integrations_screen.cancel')}
          </SecondaryButton>
          <SecondaryButton size="base" onClick={confirm}>
            {t('my_vehicles.promotion_photos_confirm')}
          </SecondaryButton>
        </div>
      </div>
    </div>
  )
}

export default PromotionPhotosModal
